/**
 * Attribution Helpers for Strava + Garmin Brand Compliance
 * 
 * Provides utility functions for determining which attribution badges
 * to display based on workout state and data sources.
 */

interface DataSource {
  type: 'cedar' | 'garmin' | 'strava' | 'apple_health' | 'google_health';
  deviceModel?: string;
  deviceManufacturer?: string;
}

interface ExternalSource {
  type: 'garmin' | 'strava' | 'apple_health' | 'google_health';
  deviceModel?: string;
  deviceManufacturer?: string;
  stravaActivityId?: string;
}

export interface DeviceLogoInfo {
  show: boolean;
  manufacturer: 'garmin' | 'apple' | null;
  variant: 'black' | 'white';
}

/**
 * Determines if push status badge should show (pre-completion)
 */
export function shouldShowPushStatus(workout: any): boolean {
  return workout.state !== 'completed' && (workout.watchPushStatus === 'pushed' || workout.watchPushStatus === 'success');
}

/**
 * Determines if data sources badges should show (post-completion)
 */
export function shouldShowDataSources(workout: any): boolean {
  return workout.state === 'completed';
}

/**
 * Gets all data sources for header badges
 * ALWAYS includes Coach Cedar (analysis/timer)
 */
export function getDataSources(session: any): DataSource[] {
  const sources: DataSource[] = [];
  
  // Always show Coach Cedar first
  sources.push({ type: 'cedar' });
  
  // 🐛 FIX (Jan 7, 2026): Check workout_data.raw_data for device info
  // 🐛 FIX (Mar 20, 2026): Also check device_data.device_name from API response
  const workoutData = session.workout_data || session.workoutData;
  const rawData = workoutData?.raw_data || workoutData?.rawData;
  const deviceData = session.device_data || session.deviceData;
  const deviceName = session.deviceName || rawData?._device_name || deviceData?.device_name;
  const deviceModel = session.deviceModel || rawData?._device_model || deviceData?.device_model || deviceData?.device_name;
  const deviceManufacturer = session.deviceManufacturer || rawData?._device_manufacturer || deviceData?.device_manufacturer;
  
  // Check if Garmin (from multiple possible sources)
  const isGarmin = 
    session.source === 'garmin' || 
    workoutData?.source === 'garmin_connect' ||
    deviceManufacturer === 'Garmin' ||
    deviceName?.toLowerCase().includes('garmin');
  
  // Add Garmin if present
  if (isGarmin) {
    sources.push({
      type: 'garmin',
      deviceModel: deviceModel,
      deviceManufacturer: deviceManufacturer || 'Garmin'
    });
  }
  
  // Add Strava if present
  if (session.source === 'strava' || workoutData?.source === 'strava') {
    sources.push({ type: 'strava' });
  }
  
  // Add Apple Health if present
  if (session.source === 'apple_health' || workoutData?.source === 'apple_health') {
    sources.push({ type: 'apple_health' });
  }

  // Add Google Health (Pixel Watch / Fitbit cloud) if present — source peer to
  // Garmin/Strava, carrying the device model for attribution.
  if (session.source === 'google_health' || workoutData?.source === 'google_health') {
    sources.push({
      type: 'google_health',
      deviceModel: deviceModel,
      deviceManufacturer: deviceManufacturer || 'Google',
    });
  }

  return sources;
}

/**
 * Gets only external sources for footer attribution
 * Excludes Coach Cedar (no "Powered by Coach Cedar" needed)
 */
export function getExternalSources(session: any): ExternalSource[] {
  const sources: ExternalSource[] = [];
  
  // 🐛 FIX (Jan 7, 2026): Check workout_data.raw_data for device info
  // 🐛 FIX (Mar 20, 2026): Also check device_data.device_name from API response
  const workoutData = session.workout_data || session.workoutData;
  const rawData = workoutData?.raw_data || workoutData?.rawData;
  const deviceData = session.device_data || session.deviceData;
  const deviceName = session.deviceName || rawData?._device_name || deviceData?.device_name;
  const deviceModel = session.deviceModel || rawData?._device_model || deviceData?.device_model || deviceData?.device_name;
  const deviceManufacturer = session.deviceManufacturer || rawData?._device_manufacturer || deviceData?.device_manufacturer;

  // Check if Garmin
  const isGarmin =
    session.source === 'garmin' ||
    workoutData?.source === 'garmin_connect' ||
    deviceManufacturer === 'Garmin' ||
    deviceName?.toLowerCase().includes('garmin');

  // Add Garmin if present
  if (isGarmin) {
    sources.push({
      type: 'garmin',
      deviceModel: deviceModel,
      deviceManufacturer: deviceManufacturer || 'Garmin'
    });
  }

  // Add Strava if present
  if (session.source === 'strava' || workoutData?.source === 'strava') {
    sources.push({
      type: 'strava',
      stravaActivityId: session.sourceId || session.stravaActivityId || rawData?.strava_activity_id
    });
  }

  // Add Google Health if present (source peer + device model).
  if (session.source === 'google_health' || workoutData?.source === 'google_health') {
    sources.push({
      type: 'google_health',
      deviceModel: deviceModel,
      deviceManufacturer: deviceManufacturer || 'Google',
    });
  }

  return sources;
}

/**
 * Checks if dual attribution needed (Garmin device via Strava)
 */
export function isDualAttribution(session: any): boolean {
  const workoutData = session.workout_data || session.workoutData;
  const rawData = workoutData?.raw_data || workoutData?.rawData;
  const deviceManufacturer = session.deviceManufacturer || rawData?._device_manufacturer;
  
  return (session.source === 'strava' || workoutData?.source === 'strava') && 
         deviceManufacturer === 'Garmin';
}

/**
 * Builds Strava activity URL for "View on Strava" link
 */
export function getStravaActivityUrl(activityId: string | number): string {
  return `https://www.strava.com/activities/${activityId}`;
}

/**
 * Determines if a device logo should show on a workout card.
 *
 * Pre-completion: show only if pushed to watch (Garmin or Apple Watch)
 * Post-completion: show if workout has Garmin or Apple Health data
 *
 * Returns manufacturer ('garmin' | 'apple') and variant ('black' | 'white')
 * based on the card background color.
 */
export function shouldShowDeviceLogo(workout: any): DeviceLogoInfo {
  const isCompleted =
    workout.state === 'completed' || workout.status === 'completed';

  if (!isCompleted) {
    // Pre-completion: only if successfully pushed to watch
    if (
      workout.watchPushStatus === 'pushed' ||
      workout.watchPushStatus === 'success'
    ) {
      const platform = workout.watchPlatform;
      const mfr: 'garmin' | 'apple' | null =
        platform === 'garmin' ? 'garmin'
        : (platform === 'apple_watch' || platform === 'apple') ? 'apple'
        : null;
      if (!mfr) return { show: false, manufacturer: null, variant: 'black' };
      // Card background is white (surface) → black logo
      return { show: true, manufacturer: mfr, variant: 'black' };
    }
    return { show: false, manufacturer: null, variant: 'black' };
  }

  // Post-completion: card bg is cedarSubtle (#E8F2ED) — still light → black logo
  const workoutData = workout.workout_data || workout.workoutData;
  const rawData = workoutData?.raw_data || workoutData?.rawData;
  const deviceData = workout.device_data || workout.deviceData;
  const deviceManufacturer =
    workout.deviceManufacturer || rawData?._device_manufacturer || deviceData?.device_manufacturer;
  const deviceName = workout.deviceName || rawData?._device_name || deviceData?.device_name;
  const source = workout.source || workoutData?.source || deviceData?.source_type;

  // Check Garmin
  const isGarmin =
    source === 'garmin' ||
    source === 'garmin_connect' ||
    workout.watchPlatform === 'garmin' ||
    deviceManufacturer === 'Garmin' ||
    deviceName?.toLowerCase().includes('garmin');

  if (isGarmin) {
    return { show: true, manufacturer: 'garmin', variant: 'black' };
  }

  // Check Apple Health / Apple Watch
  const isApple =
    source === 'apple_health' ||
    source === 'healthkit' ||
    workout.watchPlatform === 'apple_watch' ||
    deviceManufacturer?.toLowerCase().includes('apple') ||
    deviceName?.toLowerCase().includes('apple');

  if (isApple) {
    return { show: true, manufacturer: 'apple', variant: 'black' };
  }

  return { show: false, manufacturer: null, variant: 'black' };
}

/**
 * Gets external sources including Apple Health (enhanced for multi-source)
 * For multi-source workouts (e.g., Peloton via Apple Health + Garmin watch),
 * returns all contributing sources with their device models.
 */
export function getMultiSourceAttribution(session: any): ExternalSource[] {
  const sources: ExternalSource[] = [];
  const workoutData = session.workout_data || session.workoutData;
  const rawData = workoutData?.raw_data || workoutData?.rawData;
  const deviceData = session.device_data || session.deviceData;
  const deviceName = session.deviceName || rawData?._device_name || deviceData?.device_name;
  const deviceModel = session.deviceModel || rawData?._device_model || deviceData?.device_model || deviceData?.device_name;
  const deviceManufacturer = session.deviceManufacturer || rawData?._device_manufacturer || deviceData?.device_manufacturer;

  // Check Garmin
  const isGarmin =
    session.source === 'garmin' ||
    workoutData?.source === 'garmin_connect' ||
    deviceManufacturer === 'Garmin' ||
    deviceName?.toLowerCase().includes('garmin');

  if (isGarmin) {
    sources.push({
      type: 'garmin',
      deviceModel,
      deviceManufacturer: deviceManufacturer || 'Garmin',
    });
  }

  // Check Strava
  if (session.source === 'strava' || workoutData?.source === 'strava') {
    sources.push({
      type: 'strava',
      stravaActivityId:
        session.sourceId || session.stravaActivityId || rawData?.strava_activity_id,
    });
  }

  // Check Apple Health
  const isApple =
    session.source === 'apple_health' ||
    workoutData?.source === 'apple_health' ||
    deviceManufacturer?.toLowerCase().includes('apple');

  if (isApple) {
    sources.push({
      type: 'apple_health',
      deviceModel: deviceModel || rawData?._device_model,
      deviceManufacturer: deviceManufacturer || 'Apple',
    });
  }

  // Check Google Health (Pixel Watch / Fitbit cloud)
  const isGoogleHealth =
    session.source === 'google_health' || workoutData?.source === 'google_health';
  if (isGoogleHealth) {
    sources.push({
      type: 'google_health',
      deviceModel: deviceModel || rawData?._device_model,
      deviceManufacturer: deviceManufacturer || 'Google',
    });
  }

  return sources;
}






